import PropTypes from 'prop-types';
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Cookies } from 'react-cookie';
import { styled } from '@mui/material/styles';
import { Box, Drawer, Typography, Avatar, Stack, IconButton, Tooltip } from '@mui/material';
import useResponsive from '../../../hooks/useResponsive';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import Iconify from '../../../components/iconify';
import navConfig from './config';
import { NotificationContext } from '../../../context/NotificationContext';
import { fetchOrders } from '../../../pages/OrdersPage/slice/orders.slice';
import { reset, resetCourierMe } from '../../../redux/me.slice';
import { logout as authLogout } from '../../../redux/auth.slice';

const NAV_WIDTH = 264;

const StyledRoot = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.common.white,
}));

const StyledBrand = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: theme.spacing(2.5, 2.5, 2),
}));

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: theme.spacing(1.25, 1.25),
  borderRadius: 10,
  margin: theme.spacing(0, 1.5),
  backgroundColor: theme.palette.grey[100],
  border: `1px solid ${theme.palette.grey[200]}`,
}));

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cookie = new Cookies();

  const { ordersCount } = useSelector((state) => state.orders);
  const { applications } = useContext(NotificationContext);
  const isDesktop = useResponsive('up', 'lg');
  const { role } = useSelector((state) => state.auth);
  const { me } = useSelector((state) => state.me);

  useEffect(() => {
    if (role === 'VENDOR' || role === 'COURIER') {
      dispatch(fetchOrders({ status: 'pending', page: 1, page_size: 1 }));
    }
  }, [dispatch, role]);

  useEffect(() => {
    if (openNav) onCloseNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleLogout = () => {
    cookie.remove('access_token', { path: '/' });
    cookie.remove('refresh_token', { path: '/' });
    cookie.remove('role', { path: '/' });
    dispatch(authLogout());
    dispatch(reset());
    dispatch(resetCourierMe());
    navigate('/login');
  };

  const displayName = role === 'VENDOR' ? me?.name : me?.fullname;

  const renderContent = (
    <StyledRoot>
      <StyledBrand>
        <Box component="img" src="/assets/logo/logo.svg" alt="Suv Ol" sx={{ width: 36, height: 36 }} />
        <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: 18, letterSpacing: '-0.01em' }}>
          SUV OL
        </Typography>
      </StyledBrand>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <Scrollbar
          sx={{
            height: 1,
            '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
          }}
        >
          <NavSection data={navConfig(role, { ordersCount, applications })} />
        </Scrollbar>
      </Box>

      <Box sx={(theme) => ({ borderTop: `1px solid ${theme.palette.grey[200]}`, p: 1.5 })}>
        <StyledAccount>
          <Avatar src={me?.logo} alt={displayName || 'avatar'} sx={{ width: 36, height: 36 }} />
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, fontSize: 13 }}>
              {displayName || 'Foydalanuvchi'}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: 'text.secondary', fontSize: 11.5 }}>
              {me?.user?.user_type || role}
            </Typography>
          </Stack>
          <Tooltip title="Chiqish">
            <IconButton
              onClick={handleLogout}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'error.main', backgroundColor: 'error.lighter' },
              }}
            >
              <Iconify icon="solar:logout-2-bold" width={18} />
            </IconButton>
          </Tooltip>
        </StyledAccount>
      </Box>
    </StyledRoot>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: (theme) => ({
              width: NAV_WIDTH,
              backgroundColor: 'common.white',
              borderRight: `1px solid ${theme.palette.grey[200]}`,
            }),
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: NAV_WIDTH, border: 'none' } }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
